import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { translateToGuarani } from "./guarani";

export type AppLocale = "es-PY" | "gn-PY";

interface I18nContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  t: (value: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);
const storageKey = "pby-language";
const translatedAttributes = ["aria-label", "placeholder", "title"] as const;

function readInitialLocale(): AppLocale {
  try {
    return window.localStorage.getItem(storageKey) === "gn-PY" ? "gn-PY" : "es-PY";
  } catch {
    return "es-PY";
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<AppLocale>(readInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      window.localStorage.setItem(storageKey, locale);
    } catch {
      // El selector sigue funcionando aunque el navegador no guarde preferencias.
    }
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (text) => (locale === "gn-PY" ? translateToGuarani(text) : text),
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n debe utilizarse dentro de I18nProvider.");
  return context;
}

interface TranslationRecord {
  source: string;
  rendered: string;
}

export function TranslationSurface({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const rootRef = useRef<HTMLDivElement>(null);
  const textMemory = useRef(new WeakMap<Text, TranslationRecord>());
  const attributeMemory = useRef(
    new WeakMap<Element, Map<string, TranslationRecord>>(),
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const translateTextNode = (node: Text) => {
      const current = node.nodeValue ?? "";
      let record = textMemory.current.get(node);

      if (!record || current !== record.rendered) {
        record = { source: current, rendered: current };
      }

      const next =
        locale === "gn-PY" ? translateToGuarani(record.source) : record.source;
      record.rendered = next;
      textMemory.current.set(node, record);
      if (current !== next) node.nodeValue = next;
    };

    const translateElement = (element: Element) => {
      let records = attributeMemory.current.get(element);
      if (!records) {
        records = new Map();
        attributeMemory.current.set(element, records);
      }

      translatedAttributes.forEach((attribute) => {
        const current = element.getAttribute(attribute);
        if (current === null) return;
        let record = records?.get(attribute);
        if (!record || current !== record.rendered) {
          record = { source: current, rendered: current };
        }
        const next =
          locale === "gn-PY" ? translateToGuarani(record.source) : record.source;
        record.rendered = next;
        records?.set(attribute, record);
        if (current !== next) element.setAttribute(attribute, next);
      });
    };

    const translateTree = (target: Node) => {
      if (target.nodeType === Node.TEXT_NODE) {
        translateTextNode(target as Text);
        return;
      }
      if (!(target instanceof Element)) return;
      if (["SCRIPT", "STYLE", "NOSCRIPT"].includes(target.tagName)) return;
      translateElement(target);
      const walker = document.createTreeWalker(
        target,
        NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      );
      let current = walker.nextNode();
      while (current) {
        if (current.nodeType === Node.TEXT_NODE) translateTextNode(current as Text);
        else translateElement(current as Element);
        current = walker.nextNode();
      }
    };

    translateTree(root);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData") translateTree(mutation.target);
        if (mutation.type === "attributes") translateTree(mutation.target);
        mutation.addedNodes.forEach(translateTree);
      });
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...translatedAttributes],
    });

    return () => observer.disconnect();
  }, [locale]);

  return (
    <div className="translation-surface" ref={rootRef}>
      {children}
    </div>
  );
}
